import { getErrorMessage } from '../../utils/errorMessage';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import {
  ArrowRight,
  BookOpenText,
  Check,
  ChevronDown,
  Clock3,
  FolderOpen,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  Trash2,
} from 'lucide-react';
import api from '../../utils/api';
import LiquidCreateButton from '../components/LiquidCreateButton.tsx';
import PageTitleBlock from '../components/PageTitleBlock';
import WritingReviewPanel from './WritingReviewPanel';
import { writtenEpisodeCount } from './projectEpisodeCount';
import './WritingVNext.css';

const PREVIEW_PROJECTS = [
  {
    id: 'preview-project',
    title: '雾港来信',
    genre: '悬疑情感',
    current_stage: 'writing',
    episode_count: 12,
    updated_at: '2026-07-31T09:12:00.000Z',
    cover: 'violet',
  },
  {
    id: 'preview-2',
    title: '她从大婚夜逃走',
    genre: '古装逆袭',
    current_stage: 'outline',
    episode_count: 4,
    updated_at: '2026-07-30T15:20:00.000Z',
    cover: 'amber',
  },
  {
    id: 'preview-3',
    title: '电梯停在不存在的楼层',
    genre: '都市惊悚',
    current_stage: 'bible',
    episode_count: 0,
    updated_at: '2026-07-29T11:40:00.000Z',
    cover: 'blue',
  },
  {
    id: 'preview-4',
    title: '老板的失忆合约',
    genre: '都市情感',
    current_stage: 'review',
    episode_count: 24,
    updated_at: '2026-07-27T18:05:00.000Z',
    cover: 'green',
  },
];

const STAGE_META = {
  bible: { label: '构思中', next: '继续补全人物与故事规则' },
  outline: { label: '搭故事', next: '把下一段剧情铺开' },
  writing: { label: '写正文', next: '继续写下一集' },
  script: { label: '写正文', next: '继续写下一集' },
  review: { label: '打磨中', next: '看看还有哪些地方值得再磨一遍' },
};

function timeAgo(value) {
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return '最近编辑';
  const seconds = Math.max(0, (Date.now() - timestamp) / 1000);
  if (seconds < 60) return '刚刚';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} 分钟前`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} 小时前`;
  const days = Math.floor(seconds / 86400);
  return days === 1 ? '昨天' : `${days} 天前`;
}

function stageFor(project) {
  return STAGE_META[project.current_stage] || STAGE_META.bible;
}

function ProjectCover({ project, large = false, children = null }) {
  return (
    <div className={`writing-project-cover is-${project.cover || 'violet'}${large ? ' is-large' : ''}`}>
      {children || (
        <>
          <span>{project.genre || '短剧故事'}</span>
          <BookOpenText size={large ? 30 : 22} strokeWidth={1.35} />
        </>
      )}
      <i aria-hidden="true" />
    </div>
  );
}

function WritingDropdown({ label, value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const active = options.find(option => option.value === value) || options[0];

  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = event => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const closeWithEscape = event => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('keydown', closeWithEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      document.removeEventListener('keydown', closeWithEscape);
    };
  }, [open]);

  return (
    <div className={`writing-dropdown${open ? ' is-open' : ''}`} ref={rootRef}>
      <button
        type="button"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(current => !current)}
      >
        <span>{active.label}</span>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="writing-dropdown-menu" role="listbox" aria-label={`${label}选项`}>
          {options.map(option => (
            <button
              type="button"
              role="option"
              aria-selected={option.value === value}
              className={option.value === value ? 'is-selected' : ''}
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
              key={option.value}
            >
              <span>{option.label}</span>
              {option.value === value && <Check size={14} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function WritingProjectsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { preview = false } = useOutletContext() || {};
  const [projects, setProjects] = useState(preview ? PREVIEW_PROJECTS : []);
  const [loading, setLoading] = useState(!preview);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [menuProjectId, setMenuProjectId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const activeView = searchParams.get('tab') === 'review' ? 'review' : 'projects';
  const reviewProjectId = searchParams.get('projectId') || '';
  const query = searchParams.get('q') || '';
  const stage = searchParams.get('stage') || 'all';
  const sort = searchParams.get('sort') || 'updated';

  const loadProjects = async () => {
    if (preview) return;
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/agent/projects');
      setProjects(Array.isArray(response.data) ? response.data : []);
    } catch {
      setError('项目暂时没有加载出来，但你的故事没有被修改。');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview]);

  const shown = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    const result = projects.filter(project => {
      const matchesQuery = !normalized
        || `${project.title || ''} ${project.genre || ''}`.toLowerCase().includes(normalized);
      const matchesStage = stage === 'all' || project.current_stage === stage
        || (stage === 'writing' && project.current_stage === 'script');
      return matchesQuery && matchesStage;
    });
    return [...result].sort((a, b) => {
      if (sort === 'created') return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      if (sort === 'episodes') return writtenEpisodeCount(b) - writtenEpisodeCount(a);
      return new Date(b.updated_at || 0) - new Date(a.updated_at || 0);
    });
  }, [projects, query, sort, stage]);

  const recent = useMemo(
    () => [...projects].sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0))[0],
    [projects],
  );

  const updateParam = (key, value, defaultValue) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === defaultValue) next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  const changeView = nextView => {
    const next = new URLSearchParams();
    if (nextView === 'review') next.set('tab', 'review');
    setSearchParams(next, { replace: true });
  };

  const openProject = project => {
    // ⚠️ 生产环境以前跳 `/writing?open=xxx`，那是给旧 AgentLab 的写法
    // （它在一个页面里用 query 切项目）。vNext 写作台是独立路由，
    // 不改这里的话点进去会落回项目列表本身，看着像没反应。
    navigate(preview ? `/prototype/vnext/writing/${project.id}` : `/writing/${project.id}`);
  };

  const createStory = async () => {
    if (creating) return;
    setCreating(true);
    setError('');
    try {
      if (preview) {
        const project = {
          id: `preview-new-${Date.now()}`,
          title: '未命名故事',
          genre: '',
          current_stage: 'bible',
          episode_count: 0,
          updated_at: new Date().toISOString(),
          cover: 'violet',
        };
        setProjects(current => [project, ...current]);
        openProject(project);
        return;
      }
      const response = await api.post('/agent/projects', { title: '未命名故事' });
      navigate(`/writing/${response.data.id}`);
    } catch {
      setError('这次没能创建成功，可以再试一次。');
    } finally {
      setCreating(false);
    }
  };

  const deleteProject = async project => {
    setMenuProjectId(null);
    if (!window.confirm(`确定删除故事「${project.title || '未命名故事'}」吗？\n故事正文、设定和对话记录都会一并删除，且无法恢复。`)) return;
    setDeletingId(project.id);
    setError('');
    try {
      if (!preview) await api.delete(`/agent/projects/${project.id}`);
      setProjects(current => current.filter(item => String(item.id) !== String(project.id)));
    } catch {
      setError('这个故事暂时没有删除成功，可以稍后再试一次。');
    } finally {
      setDeletingId(null);
    }
  };

  const renameProject = async project => {
    setMenuProjectId(null);
    const input = window.prompt('修改故事名称', project.title || '未命名故事');
    if (input == null) return;
    const title = input.trim();
    if (!title) {
      setError('故事名称不能为空。');
      return;
    }
    if (title === project.title) return;

    setRenamingId(project.id);
    setError('');
    try {
      const updated = preview
        ? { ...project, title, updated_at: new Date().toISOString() }
        : (await api.patch(`/agent/projects/${project.id}`, { title })).data;
      setProjects(current => current.map(item => String(item.id) === String(project.id)
        ? { ...item, ...updated, title }
        : item));
    } catch (requestError) {
      setError(getErrorMessage(requestError, '故事名称暂时没有改成功，可以稍后再试一次。'));
    } finally {
      setRenamingId(null);
    }
  };

  return (
    <div className={`writing-projects-page vnext-page-surface is-writing-library${activeView === 'projects' ? ' is-story-library' : ' is-story-review'}`}>
      <div className="writing-projects-shell vnext-page-frame">
        <header className="writing-projects-heading">
          <PageTitleBlock
            title={activeView === 'review' ? '审核剧本' : '我的故事'}
          />
          {activeView === 'projects' && (
            <LiquidCreateButton
              variant="lavender"
              label="新建剧本"
              loading={creating}
              loadingLabel="正在创建"
              leadingIcon={<Plus size={16} />}
              showArrow={false}
              onClick={createStory}
            />
          )}
        </header>

        <nav className="writing-project-tabs" aria-label="剧本管理">
          <button type="button" className={activeView === 'projects' ? 'is-active' : ''} aria-current={activeView === 'projects' ? 'page' : undefined} onClick={() => changeView('projects')}>
            剧本项目
          </button>
          <button type="button" className={activeView === 'review' ? 'is-active' : ''} aria-current={activeView === 'review' ? 'page' : undefined} onClick={() => changeView('review')}>
            审核剧本
          </button>
        </nav>

        {activeView === 'projects' && recent && !loading && (
          <section className="writing-continue-card" aria-labelledby="continue-title">
            <ProjectCover project={recent} large />
            <div className="writing-continue-copy">
              <span>CONTINUE WRITING</span>
              <h2 id="continue-title">{recent.title}</h2>
              <p>{stageFor(recent).next}</p>
              <div>
                <em>{stageFor(recent).label}</em>
                {writtenEpisodeCount(recent) > 0 && <em>{writtenEpisodeCount(recent)} 集</em>}
                <em><Clock3 size={12} /> {timeAgo(recent.updated_at)}</em>
              </div>
            </div>
            <button type="button" onClick={() => openProject(recent)}>
              继续创作 <ArrowRight size={15} />
            </button>
          </section>
        )}

        {activeView === 'projects' ? <section className="writing-library" aria-labelledby="story-library-title">
          <div className="writing-library-top">
            <div>
              <span>STORY LIBRARY</span>
              <h2 id="story-library-title">全部故事</h2>
            </div>
            <div className="writing-library-tools">
              <label className="writing-search">
                <Search size={14} />
                <input
                  value={query}
                  onChange={event => updateParam('q', event.target.value, '')}
                  placeholder="搜索故事或题材"
                  aria-label="搜索故事或题材"
                />
              </label>
              <WritingDropdown
                label="阶段筛选"
                value={stage}
                onChange={value => updateParam('stage', value, 'all')}
                options={[
                  { value: 'all', label: '全部阶段' },
                  { value: 'bible', label: '构思中' },
                  { value: 'outline', label: '搭故事' },
                  { value: 'writing', label: '写正文' },
                  { value: 'review', label: '打磨中' },
                ]}
              />
              <WritingDropdown
                label="项目排序"
                value={sort}
                onChange={value => updateParam('sort', value, 'updated')}
                options={[
                  { value: 'updated', label: '最近编辑' },
                  { value: 'created', label: '最早创建' },
                  { value: 'episodes', label: '集数较多' },
                ]}
              />
            </div>
          </div>

          {error && (
            <div className="writing-inline-error" role="alert">
              <span>{error}</span>
              <button type="button" onClick={loadProjects}>重新加载</button>
            </div>
          )}

          {loading ? (
            <div className="writing-project-grid" aria-label="正在加载故事">
              {[0, 1, 2, 3, 4, 5].map(item => <div className="writing-project-skeleton" key={item} />)}
            </div>
          ) : shown.length ? (
            <div className="writing-project-grid">
              {shown.map(project => (
                <article className="writing-project-card" key={project.id}>
                  <button type="button" onClick={() => openProject(project)} aria-label={`继续创作 ${project.title}`}>
                    <ProjectCover project={project}>
                      <div className="writing-project-card-top">
                        <span>{project.genre || '题材待定'}</span>
                        <em>{stageFor(project).label}</em>
                      </div>
                      <div className="writing-project-card-bottom">
                        <h3>{project.title}</h3>
                        <div>
                          <span>{writtenEpisodeCount(project) ? `${writtenEpisodeCount(project)} 集` : '尚未写正文'} · {timeAgo(project.updated_at)}</span>
                          <strong>继续创作 <ArrowRight size={14} /></strong>
                        </div>
                      </div>
                    </ProjectCover>
                  </button>
                  <div className={`writing-project-card-menu${menuProjectId === project.id ? ' is-open' : ''}`}>
                    <button
                      type="button"
                      className="writing-project-card-menu-trigger"
                      aria-label={`更多操作：${project.title}`}
                      aria-haspopup="menu"
                      aria-expanded={menuProjectId === project.id}
                      disabled={deletingId === project.id || renamingId === project.id}
                      onClick={() => setMenuProjectId(current => current === project.id ? null : project.id)}
                    >
                      {deletingId === project.id || renamingId === project.id
                        ? <Loader2 size={15} className="animate-spin" />
                        : <MoreHorizontal size={17} />}
                    </button>
                    {menuProjectId === project.id && (
                      <>
                        <button type="button" className="writing-project-card-menu-backdrop" aria-label="关闭故事操作菜单" onClick={() => setMenuProjectId(null)} />
                        <div className="writing-project-card-menu-popover" role="menu" aria-label={`${project.title}的操作`}>
                          <button type="button" role="menuitem" onClick={() => renameProject(project)}>
                            <Pencil size={14} />
                            修改名称
                          </button>
                          <button type="button" role="menuitem" onClick={() => deleteProject(project)}>
                            <Trash2 size={14} />
                            删除故事
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="writing-project-empty">
              <FolderOpen size={24} />
              <h3>{query ? `没找到「${query}」` : '这里还没有故事'}</h3>
              <p>{query ? '换个词，或者清除筛选再找找。' : '先写下一句话，剩下的我们慢慢搭。'}</p>
              <button type="button" disabled={creating} onClick={() => query ? setSearchParams({}) : createStory()}>
                {query ? '清除筛选' : '开始第一个故事'}
              </button>
            </div>
          )}
        </section> : (
          <WritingReviewPanel
            projects={projects}
            preview={preview}
            initialProjectId={reviewProjectId}
            initialMode={searchParams.get('mode')}
            onProjectCreated={loadProjects}
          />
        )}
      </div>

    </div>
  );
}
